import {
  Badge,
  Button,
  Divider,
  Group,
  Paper,
  Text,
  Tooltip,
} from '@mantine/core';
import { PiCheck, PiPlus, PiWarningCircle } from 'react-icons/pi';
import { TOffer } from '@/models/offers';
import { SplitDerived, money } from './splitDerive';

/**
 * The pricing/COGS header row for the split-schedule card. Absorbs:
 *  - the COGS bar + ordered total + distributed badge (UX-006),
 *  - the Discount field (UX-001),
 *  - the "Set inst1 = COGS" and "Add installment" actions (UX-003),
 * into one aligned row at the top of the schedule. Left sub-group = read-only
 * figures in decision order; right sub-group = discount + actions.
 *
 * When `committed`, renders a read-only summary line only (no discount, no actions).
 */
export default function PricingHeader({
  derived: d,
  currency,
  offer,
  editable,
  committed = false,
  showCogs = true,
  onSetFirstToCogs,
  onAddInstallment,
  canSetFirstToCogs,
  showAddInstallment = true,
}: {
  derived: SplitDerived;
  currency: string;
  offer?: TOffer;
  editable: boolean;
  committed?: boolean;
  /** COGS figures are for BizDev review only — hidden in the SR builder. */
  showCogs?: boolean;
  onSetFirstToCogs: () => void;
  onAddInstallment: () => void;
  canSetFirstToCogs: boolean;
  showAddInstallment?: boolean;
}) {
  return (
    <Paper
      withBorder
      p="xs"
      bg="var(--mantine-color-gray-light)"
      data-testid="ux-pricing-header"
    >
      <Group justify="flex-start" align="flex-end" wrap="wrap" gap="xs">
        {/* Pricing sub-cluster: what the customer pays → discount → COGS total */}
        <Tooltip label="What the customer agreed to pay, before VAT. VAT and any payment fee are added per invoice, once they choose how they pay.">
          <Text fz="sm" fw={500}>
            Agreed total {money(d.orderedTotal, currency)}
          </Text>
        </Tooltip>
        {offer && Number(offer.discountPercentage) > 0 && (
          <Tooltip label="Already taken off the agreed total. Locked once the customer confirms their selection.">
            <Badge size="sm" variant="light" color="gray">
              Discount {Number(offer.discountPercentage)}% applied
            </Badge>
          </Tooltip>
        )}
        {showCogs && (
          <>
            <Text fz="sm" c="dimmed">
              Total COGS {money(d.cogsTotal, currency)}
            </Text>

            <Divider orientation="vertical" />

            {/* Coverage sub-cluster: the covers metric → the action that satisfies it */}
            <Text fz="sm">
              Inst. 1 covers {money(d.firstInstallmentCogs, currency)}
            </Text>
            <Badge
              size="sm"
              variant="light"
              color={d.cogsCovered ? 'teal' : 'orange'}
              leftSection={
                d.cogsCovered ? (
                  <PiCheck size={12} />
                ) : (
                  <PiWarningCircle size={12} />
                )
              }
            >
              {d.cogsCovered ? 'COGS covered' : 'COGS not covered'}
            </Badge>
            {!committed && editable && (
              <Tooltip label="Assign every service to installment 1 so it covers the full order COGS">
                <Button
                  size="xs"
                  variant="light"
                  onClick={onSetFirstToCogs}
                  disabled={!canSetFirstToCogs}
                >
                  Set inst. 1 = COGS
                </Button>
              </Tooltip>
            )}
          </>
        )}

        <Divider orientation="vertical" />

        {/* Reconciliation sub-cluster: billed sum vs order total → add-installment */}
        <Tooltip label="The sum of installment amounts must equal the order total">
          <Badge
            size="sm"
            variant="light"
            color={d.amountsSumToTotal ? 'teal' : 'red'}
            leftSection={
              d.amountsSumToTotal ? (
                <PiCheck size={12} />
              ) : (
                <PiWarningCircle size={12} />
              )
            }
          >
            Σ {money(d.amountsTotal, currency)} /{' '}
            {money(d.orderedTotal, currency)}
          </Badge>
        </Tooltip>
        {!committed && editable && showAddInstallment && (
          <Button
            size="xs"
            variant="light"
            leftSection={<PiPlus size={12} />}
            onClick={onAddInstallment}
            data-testid="ux-add-installment"
          >
            Add installment
          </Button>
        )}
      </Group>
    </Paper>
  );
}
